import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { PauseIcon, PlayIcon, StopIcon } from '@phosphor-icons/react';
import { useLiveRecording } from '../LiveRecordingContext';

const WAVE_BARS = [0.4, 0.7, 1, 0.6, 0.9, 0.5, 0.8, 0.45, 0.75, 0.55] as const;

function AudioWave() {
  return (
    <div className="flex items-center gap-[2px] h-4">
      {WAVE_BARS.map((scale, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-white/80"
          style={{
            animation: `recording-wave 1.2s ease-in-out ${i * 0.1}s infinite alternate`,
            height: `${scale * 100}%`
          }}
        />
      ))}
      <style>{`
        @keyframes recording-wave {
          0% { transform: scaleY(0.3); }
          100% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}

interface RecordingBannerProps {
  currentMeetingId?: string;
}

export default function RecordingBanner({ currentMeetingId }: RecordingBannerProps) {
  const { isRecording, isPaused, meetingId, stopRecording, pauseRecording, resumeRecording } = useLiveRecording();
  const navigate = useTenantNavigate();

  if (!isRecording || !meetingId) return null;

  const isOnRecordingMeeting = currentMeetingId === meetingId;

  return (
    <div
      className={`sticky top-0 z-30 text-white px-4 py-2 flex items-center justify-between gap-4 shadow-md ${isPaused ? 'bg-amber-600' : 'bg-red-600'}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm font-medium shrink-0">{isPaused ? 'Recording paused' : 'Recording'}</span>
        {!isPaused && <AudioWave />}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isOnRecordingMeeting && (
          <button
            className="text-xs font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
            onClick={() => navigate(`/meetings/${meetingId}`)}
          >
            Back to meeting
          </button>
        )}
        <button
          className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
          onClick={isPaused ? resumeRecording : pauseRecording}
        >
          {isPaused ? <PlayIcon size={14} weight="bold" /> : <PauseIcon size={14} weight="bold" />}
          {isPaused ? 'Resume' : 'Pause'}
        </button>
        <button
          className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
          onClick={stopRecording}
        >
          <StopIcon size={14} weight="bold" />
          Finish
        </button>
      </div>
    </div>
  );
}
